import React, { useMemo, useState } from "react";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { usePolling, usePrice } from "@/components/croc/hooks";
import { Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtUsd, fmtNum, pct } from "@/lib/format";
import api from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Calculator as CalcIcon, TrendingUp } from "lucide-react";

export default function Calculator() {
  const { data: config } = usePolling(() => api.config(), [], 120000);
  const price = usePrice();
  const [amount, setAmount] = useState(2500);
  const [tierKey, setTierKey] = useState("360d");
  const [compound, setCompound] = useState(true);
  const [predictDaily, setPredictDaily] = useState(false);

  const tiers = config?.tiers ?? [];
  const tier = tiers.find((t) => t.key === tierKey) || tiers[0];

  const { series, summary } = useMemo(() => {
    if (!tier) return { series: [], summary: {} };
    const rate = tier.daily_rate * (predictDaily ? 2 : 1); // prediction bonus doubles the daily reward
    const days = tier.days;
    const cap = amount * tier.max_roi;
    const pts = [];
    let bal = amount;
    let simple = 0;
    const step = Math.max(1, Math.floor(days / 40));
    for (let d = 0; d <= days; d += step) {
      simple = Math.min(amount * rate * d, cap);
      const comp = Math.min(amount * Math.pow(1 + rate, d) - amount, cap);
      pts.push({ day: d, simple: +simple.toFixed(2), compound: +comp.toFixed(2) });
      bal = amount + comp;
    }
    const finalSimple = Math.min(amount * rate * days, cap);
    const finalComp = Math.min(amount * Math.pow(1 + rate, days) - amount, cap);
    const reward = compound ? finalComp : finalSimple;
    const apr = rate * 365 * 100;
    const apy = (Math.pow(1 + rate, 365) - 1) * 100;
    const breakeven = rate > 0 ? Math.ceil(1 / rate) : 0; // days for 100% via daily rate (uncapped ref)
    return {
      series: pts,
      summary: { reward, total: amount + reward, apr, apy, breakeven, dailyReward: amount * rate, cap },
    };
  }, [amount, tier, compound, predictDaily]);

  return (
    <div>
      <SectionTitle
        eyebrow="Dynamic Yield & Compound Calculator"
        title="Daily Rewards Calculator"
        subtitle="Project earnings across tiers. Figures are illustrative — actual yield floats with realized revenue."
        testid="calculator-title"
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-1" data-testid="calculator-controls">
          <div className="flex items-center gap-2 mb-5">
            <CalcIcon className="w-4 h-4 gold-text" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Inputs</span>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-muted-foreground">Stake amount</span>
              <span className="font-num font-bold text-white">{fmtUsd(amount, 0)}</span>
            </div>
            <Slider
              data-testid="roi-calculator-amount-slider"
              value={[amount]}
              min={100}
              max={100000}
              step={100}
              onValueChange={(v) => setAmount(v[0])}
            />
            <div className="text-[11px] text-muted-foreground mt-2 font-num">
              ≈ {fmtNum(price?.price ? amount / price.price : 0, 0)} CROC
            </div>
          </div>

          <div className="mb-6">
            <span className="text-sm text-muted-foreground">Tier</span>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {tiers.map((t) => (
                <button
                  key={t.key}
                  data-testid={`calc-tier-${t.key}`}
                  onClick={() => setTierKey(t.key)}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors border ${
                    tierKey === t.key ? "border-[#1CFD4C]/50 bg-[#1CFD4C]/10 text-white" : "border-white/10 text-muted-foreground hover:text-white"
                  }`}
                >
                  <span>{t.name}</span>
                  <span className="font-num gold-text">{pct(t.daily_rate * 100)}/d</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3">
            <span className="text-sm text-white">Compound daily</span>
            <Switch data-testid="calc-compound-toggle" checked={compound} onCheckedChange={setCompound} />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-[#1CFD4C]/25 bg-[#1CFD4C]/[0.05] px-4 py-3 mt-3">
            <div>
              <span className="text-sm text-white">Predict daily</span>
              <div className="text-[10px] text-muted-foreground">Answer all predictions → daily reward ×2</div>
            </div>
            <Switch data-testid="calc-predict-toggle" checked={predictDaily} onCheckedChange={setPredictDaily} />
          </div>
        </Panel>

        <div className="lg:col-span-2 space-y-6 min-w-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Big label="Projected Reward" value={fmtUsd(summary.reward)} accent />
            <Big label="Total Value" value={fmtUsd(summary.total)} />
            <Big label="APR" value={pct(summary.apr, 0)} />
            <Big label="APY (compound)" value={pct(summary.apy, 0)} />
          </div>

          <Panel data-testid="calculator-chart">
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-bold text-white">Growth Projection · {tier?.days}d</span>
              <Pill tone="emerald"><TrendingUp className="w-3 h-3" /> {compound ? "Compounding" : "Simple"}{predictDaily ? " · ×2 prediction" : ""}</Pill>
            </div>
            <div className="h-64 w-full min-w-0">
              <ResponsiveContainer width="99%" height={256}>
                <LineChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="day" stroke="#6B7280" fontSize={11} tickLine={false} />
                  <YAxis stroke="#6B7280" fontSize={11} tickLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: "#0F1712", border: "1px solid rgba(28,253,76,0.3)", borderRadius: 12 }}
                    formatter={(v, n) => [`$${Number(v).toFixed(2)}`, n === "compound" ? "Compound" : "Simple"]}
                    labelFormatter={(l) => `Day ${l}`}
                  />
                  <Line type="monotone" dataKey="simple" stroke="#1CFD4C" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                  <Line type="monotone" dataKey="compound" stroke="#34D399" strokeWidth={2.5} dot={false} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
              <Row label="Daily reward" value={fmtUsd(summary.dailyReward)} />
              <Row label="Rewards cap" value={fmtUsd(summary.cap)} />
              <Row label="Break-even (est.)" value={`${summary.breakeven || 0}d`} />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Big({ label, value, accent }) {
  return (
    <div className="croc-panel p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-num font-bold text-xl mt-1 ${accent ? "emerald-text" : "text-white"}`}>{value}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#090E0B] p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-num font-bold text-white mt-1">{value}</div>
    </div>
  );
}
